import { ILeaderboard, TimeFrame } from '@/models/Leaderboard'

import { GokollabService } from '@/service/GokollabService'
import restAgent from '@/service/restAgent'
import { useGlobalStore } from '@/store/app'
import { waitForStoreIds } from '@/util/storeHelpers'
import { isGokollab } from '@ghl/clientportal-core/init'
import { reactive } from 'vue'

export class LeaderboardsState {
  state = reactive({
    isLoading: false,
    leaderboard7d: [] as ILeaderboard[],
    leaderboard30d: [] as ILeaderboard[],
    leaderboardAllTime: [] as ILeaderboard[],
    lastUpdatedAt: '',
  })

  // Helper method to get leaderboard data from appropriate service
  private async getLeaderboardData(
    groupId: string,
    timeframe: TimeFrame | undefined,
    rankLimit: number,
    rankSkip: number
  ): Promise<any> {
    const response = isGokollab()
      ? await GokollabService.getLeaderboard(
          groupId,
          timeframe,
          rankLimit,
          rankSkip
        )
      : await restAgent.ClientPortal.getLeaderboard(
          groupId,
          timeframe,
          rankLimit,
          rankSkip
        )
    return response
  }

  clearState(): void {
    this.state.leaderboard7d = []
    this.state.leaderboard30d = []
    this.state.leaderboardAllTime = []
    this.state.lastUpdatedAt = ''
    this.state.isLoading = false
  }

  async fetchLeaderboard(timeFrame?: TimeFrame): Promise<void> {
    try {
      let lastUpdateDate: string
      let leaderboard
      await waitForStoreIds()
      switch (timeFrame) {
        case TimeFrame.SEVEN_DAY:
          if (this.state.leaderboard7d.length !== 0) {
            return
          }
          this.state.isLoading = true
          leaderboard = await this.getLeaderboardData(
            useGlobalStore().$state.groupId,
            TimeFrame.SEVEN_DAY,
            10,
            0
          )
          if (leaderboard[TimeFrame.SEVEN_DAY]) {
            this.state.leaderboard7d = leaderboard[TimeFrame.SEVEN_DAY]
            lastUpdateDate = this.formateDate(
              this.state.leaderboard7d[0].updatedAt
            )
            if (this.state.lastUpdatedAt !== lastUpdateDate) {
              this.state.lastUpdatedAt = lastUpdateDate
              // clean up the other leaderboards
              this.state.leaderboard30d = []
              this.state.leaderboardAllTime = []
            }
          }

          this.state.isLoading = false
          break
        case TimeFrame.THIRTY_DAY:
          if (this.state.leaderboard30d.length !== 0) {
            return
          }
          leaderboard = await this.getLeaderboardData(
            useGlobalStore().$state.groupId,
            TimeFrame.THIRTY_DAY,
            10,
            0
          )
          if (leaderboard && leaderboard[TimeFrame.THIRTY_DAY]) {
            this.state.leaderboard30d = leaderboard[TimeFrame.THIRTY_DAY]
            lastUpdateDate = this.formateDate(
              this.state.leaderboard30d[0].updatedAt
            )
            if (this.state.lastUpdatedAt !== lastUpdateDate) {
              this.state.lastUpdatedAt = lastUpdateDate
              // clean up the other leaderboards
              this.state.leaderboard7d = []
              this.state.leaderboardAllTime = []
            }
          }

          this.state.isLoading = false
          break
        case TimeFrame.ALL_TIME:
          if (this.state.leaderboardAllTime.length !== 0) {
            return
          }
          leaderboard = await this.getLeaderboardData(
            useGlobalStore().$state.groupId,
            TimeFrame.ALL_TIME,
            10,
            0
          )
          if (leaderboard && leaderboard[TimeFrame.ALL_TIME]) {
            this.state.leaderboardAllTime = leaderboard[TimeFrame.ALL_TIME]
            lastUpdateDate = this.formateDate(
              this.state.leaderboardAllTime[0].updatedAt
            )
            if (this.state.lastUpdatedAt !== lastUpdateDate) {
              this.state.lastUpdatedAt = lastUpdateDate
              // clean up the other leaderboards
              this.state.leaderboard30d = []
              this.state.leaderboard7d = []
            }
          }

          this.state.isLoading = false
          break
        default:
          // when time frame is not passed fetch all leaderboards
          leaderboard = await this.getLeaderboardData(
            useGlobalStore().$state.groupId,
            undefined,
            10,
            0
          )
          if (leaderboard.data) {
            this.state.leaderboardAllTime = leaderboard.data[TimeFrame.ALL_TIME]
            this.state.leaderboard30d = leaderboard.data[TimeFrame.THIRTY_DAY]
            this.state.leaderboard7d = leaderboard.data[TimeFrame.SEVEN_DAY]

            this.state.lastUpdatedAt = this.formateDate(
              this.state.leaderboardAllTime?.at(0)?.updatedAt
            )
          }
          break
      }
    } catch (error) {
      console.error(error)
      this.state.isLoading = false
      throw error
    }
  }

  formateDate(stringDate?: string): string {
    if (!stringDate) return ''
    const date = new Date(stringDate)
    const formattedDate = date
      .toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      })
      .replace(/,/g, '')
      .replace(' AM', 'am')
      .replace(' PM', 'pm')
    return formattedDate
  }
}

export const LeaderboardState = new LeaderboardsState()
